import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  PlayIcon,
  CalendarDaysIcon,
  CloudIcon,
  ChartBarIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { formatDateTime, formatTradingDay, statusBadge, formatBytes } from './dailyDataUtils'
import BatchIngestProgressModal from './BatchIngestProgressModal'

const FEEDS = {
  sessions: {
    title: 'Gaming sessions',
    description: 'Import FactPlayerSession extracts into gaming_sessions.',
    extractLabel: 'FactPlayerSession',
    icon: ChartBarIcon,
    enabledKey: 'sessions_enabled',
  },
  meter: {
    title: 'Meter readings',
    description: 'Import Meter extracts into egm_meter_readings.',
    extractLabel: 'Meter',
    icon: CpuChipIcon,
    enabledKey: 'meter_enabled',
  },
}

export default function AzureExtractFeed({ feedId }) {
  const feed = FEEDS[feedId]
  if (!feed) return null

  const apiBase = `/api/daily-data/${feedId}`
  const Icon = feed.icon

  const [data, setData] = useState(null)
  const [azureSources, setAzureSources] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadingAzure, setLoadingAzure] = useState(false)
  const [running, setRunning] = useState(false)
  const [message, setMessage] = useState(null)
  const [tradingDay, setTradingDay] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [expandedFolder, setExpandedFolder] = useState(null)
  const [azureNav, setAzureNav] = useState({ year: null, month: null })
  const [batchIngest, setBatchIngest] = useState(null)

  const fetchAzureSources = async ({ year, month } = {}) => {
    const params = new URLSearchParams()
    if (year) params.set('year', year)
    if (month) params.set('month', month)
    const qs = params.toString()
    const res = await authFetch(getLocalApiUrl(`${apiBase}/sources${qs ? `?${qs}` : ''}`))
    const json = await res.json()
    if (!json.success) throw new Error(json.error || 'Failed to list Azure extracts')
    return json.data
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl(`${apiBase}?days=14`))
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to load feed')
      setData(json.data)
      setTradingDay(prev => prev || json.data?.trading_day_yesterday || json.data?.trading_day_today || '')
      setTemplateId(prev => prev || (json.data?.default_template_id ? String(json.data.default_template_id) : ''))
    } catch (e) {
      setMessage({ type: 'error', text: e.message })
    } finally {
      setLoading(false)
    }
  }, [apiBase])

  const loadAzure = useCallback(async (nav = {}) => {
    setLoadingAzure(true)
    try {
      let year = nav.year ?? null
      let month = nav.month ?? null
      if (nav.level === 'root') { year = null; month = null }
      else if (nav.level === 'year') month = null

      if (!nav.explicit && !year) {
        const root = await fetchAzureSources({})
        if (root.years?.length) year = root.years[0].year
      }
      if (!nav.explicit && year && !month) {
        const monthsView = await fetchAzureSources({ year })
        if (monthsView.months?.length) month = monthsView.months[0].month_key
      }

      const blobData = year && month
        ? await fetchAzureSources({ year, month })
        : year
          ? await fetchAzureSources({ year })
          : await fetchAzureSources({})

      setAzureSources(blobData)
      setAzureNav({ year: blobData.year || year || null, month: blobData.month || month || null })
    } catch (e) {
      setMessage({ type: 'error', text: e.message })
    } finally {
      setLoadingAzure(false)
    }
  }, [apiBase])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (data?.config?.source_type === 'azure_blob' && !azureSources) loadAzure()
  }, [data, azureSources, loadAzure])

  const runImport = async (opts = {}) => {
    setRunning(true)
    setMessage(null)
    try {
      const body = {}
      if (opts.tradingDay) body.trading_day = opts.tradingDay
      if (opts.blobPath) body.blob_path = opts.blobPath
      if (templateId) body.template_id = parseInt(templateId, 10)
      if (opts.importAllBatches) {
        body.import_all_batches = true
        if (opts.folderPath) body.folder_path = opts.folderPath
      }
      const res = await authFetch(getLocalApiUrl(`${apiBase}/run`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Import failed')
      const d = json.data
      setMessage({
        type: d.errors > 0 && d.imported === 0 ? 'error' : d.errors > 0 ? 'warning' : 'success',
        summary: `Import complete — ${d.imported} imported, ${d.skipped ?? 0} skipped, ${d.errors} errors (${d.duration_ms}ms)`,
        errorDetails: d.error_details?.length ? d.error_details : (d.error_message ? d.error_message.split('\n') : []),
      })
      await load()
      if (data?.config?.source_type === 'azure_blob') await loadAzure({ explicit: true, year: azureNav.year, month: azureNav.month })
    } catch (e) {
      setMessage({ type: 'error', text: e.message })
    } finally {
      setRunning(false)
    }
  }

  const isAzure = data?.config?.source_type === 'azure_blob'
  const cfg = data?.config
  const enabled = cfg?.[feed.enabledKey] !== false
  const azureDayFolders = azureSources?.days?.length ? azureSources.days : (azureSources?.sources || [])
  const importBusy = running || !!batchIngest

  const navigateAzure = (level, year = null, month = null) => {
    setExpandedFolder(null)
    loadAzure({ explicit: true, level, year, month })
  }

  const startBatchIngest = (folder) => {
    if (!folder?.files?.length) return
    setBatchIngest({
      files: folder.files,
      tradingDay: folder.trading_day,
      folderPath: folder.folder_path,
      folderName: folder.folder_name,
    })
  }

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <Link to="/monitor-safe/daily-data" className="inline-flex items-center hover:text-gray-900">
          <ArrowLeftIcon className="w-4 h-4 mr-1" />
          Daily Data
        </Link>
        <span>/</span>
        <span className="font-medium text-gray-900">{feed.title}</span>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-primary-50 p-2 border border-primary-100">
              <Icon className="w-7 h-7 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{feed.title}</h1>
              <p className="text-gray-600 mt-1">{feed.description}</p>
              <p className="text-xs text-gray-500 mt-2">
                Source: <span className="font-medium">{cfg?.source_type || 'local'}</span>
                {isAzure && azureSources?.account_name && (
                  <> — {azureSources.account_name}/{azureSources.container}/{azureSources.prefix}</>
                )}
                {!enabled && <span className="ml-2 text-amber-700">(disabled in import config)</span>}
              </p>
            </div>
          </div>
          <button type="button" onClick={load} className="inline-flex items-center px-3 py-2 border rounded-md text-sm hover:bg-gray-50">
            <ArrowPathIcon className="w-4 h-4 mr-1" />
            Refresh
          </button>
        </div>
      </div>

      {!enabled && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {feed.title} import is disabled. Enable it in{' '}
          <Link to="/monitor-safe/daily-data/settings" className="underline font-medium">Daily Data settings</Link>.
        </div>
      )}

      {message && (
        <div className={`rounded-lg border p-4 text-sm ${
          message.type === 'success' ? 'bg-green-50 border-green-200 text-green-900'
            : message.type === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-900'
              : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <p>{message.summary || message.text}</p>
        </div>
      )}

      {(isAzure || azureSources) && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <CloudIcon className="w-5 h-5 text-sky-600" />
              Available in Azure (TPDataExtracts)
            </h2>
            <button type="button" onClick={() => loadAzure({ explicit: true, year: azureNav.year, month: azureNav.month })} disabled={loadingAzure} className="text-sm text-primary-600 hover:underline disabled:opacity-50">
              {loadingAzure ? 'Loading…' : 'Refresh list'}
            </button>
          </div>

          {azureSources?.configured && (
            <>
              <nav className="flex flex-wrap items-center gap-1 text-sm text-gray-600 mb-4">
                <button type="button" onClick={() => navigateAzure('root')} className="font-mono text-primary-600 hover:underline">TPDataExtracts</button>
                {azureNav.year && (
                  <>
                    <span>/</span>
                    <button type="button" onClick={() => navigateAzure('year', azureNav.year)} className="font-mono text-primary-600 hover:underline">{azureNav.year}</button>
                  </>
                )}
                {azureNav.year && azureNav.month && (
                  <>
                    <span>/</span>
                    <span className="font-mono text-gray-900">{azureNav.month}</span>
                  </>
                )}
              </nav>

              {azureSources.level === 'days' && (
                <>
                  <p className="text-xs text-gray-500 mb-3">
                    {azureDayFolders.length} day folder(s) · {azureDayFolders.filter(d => d.has_extract).length} with {feed.extractLabel} files
                  </p>
                  <div className="space-y-2">
                    {azureDayFolders.map(folder => (
                      <div key={folder.folder_path} className="border rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setExpandedFolder(expandedFolder === folder.folder_path ? null : folder.folder_path)}
                          className="w-full flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left"
                        >
                          <div>
                            <span className="font-medium text-gray-900">
                              {folder.trading_day ? formatTradingDay(folder.trading_day) : folder.folder_name}
                            </span>
                            {folder.folder_name && folder.trading_day && (
                              <span className="ml-2 text-sm font-mono text-gray-500">{folder.folder_name}</span>
                            )}
                            {!folder.has_extract && (
                              <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">No {feed.extractLabel}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500">{folder.file_count || 0} file(s){folder.total_bytes ? ` · ${formatBytes(folder.total_bytes)}` : ''}</span>
                            {folder.has_extract && (
                              <button
                                type="button"
                                disabled={importBusy || !enabled}
                                onClick={(e) => { e.stopPropagation(); startBatchIngest(folder) }}
                                className="px-2 py-1 bg-primary-600 text-white rounded text-xs hover:bg-primary-700 disabled:opacity-50"
                              >
                                Ingest all
                              </button>
                            )}
                          </div>
                        </button>
                        {expandedFolder === folder.folder_path && folder.files?.length > 0 && (
                          <ul className="divide-y divide-gray-100">
                            {folder.files.map(file => (
                              <li key={file.blob_path} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm">
                                <span className="font-mono text-gray-800 truncate max-w-xl" title={file.blob_path}>{file.name}</span>
                                <span className="text-gray-500">{formatBytes(file.size)}</span>
                                <button
                                  type="button"
                                  disabled={importBusy || !enabled}
                                  onClick={() => runImport({ blobPath: file.blob_path, tradingDay: folder.trading_day })}
                                  className="text-primary-600 hover:underline font-medium disabled:opacity-50"
                                >
                                  Ingest
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Import by trading day</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <CalendarDaysIcon className="w-4 h-4 inline mr-1" />
              Trading day
            </label>
            <input type="date" value={tradingDay} onChange={(e) => setTradingDay(e.target.value)} className="w-full border rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mapping template</label>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm"
            >
              <option value="">Default template</option>
              {(data?.templates || []).map(t => (
                <option key={t.id} value={t.id}>
                  {t.template_name}{t.gaming_machine_vendor ? ` — ${t.gaming_machine_vendor}` : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              <Link to="/monitor-safe/daily-data/settings" className="text-primary-600 hover:underline">Settings</Link>
              {' · '}
              <Link to="/admin/group-config" className="text-primary-600 hover:underline">Edit mappings</Link>
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={importBusy || !enabled || !tradingDay}
            onClick={() => runImport({ tradingDay })}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md text-sm hover:bg-primary-700 disabled:opacity-50"
          >
            <PlayIcon className="w-4 h-4 mr-1" />
            {running ? 'Importing…' : 'Import trading day'}
          </button>
          <button
            type="button"
            disabled={importBusy || !enabled}
            onClick={() => runImport({ tradingDay: 'yesterday' })}
            className="inline-flex items-center px-4 py-2 border rounded-md text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            Import yesterday
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Recent trading days</h2>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Day</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Imported</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Azure</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(data?.trading_days || []).map(row => (
              <tr key={row.trading_day}>
                <td className="px-4 py-2 text-sm">{formatTradingDay(row.trading_day)}</td>
                <td className="px-4 py-2 text-sm">{statusBadge(row.status)}</td>
                <td className="px-4 py-2 text-sm text-right">{row.imported ?? '—'}</td>
                <td className="px-4 py-2 text-sm text-right text-gray-600">
                  {row.azure_blob_available ? `${row.azure_file_count} file(s)` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {batchIngest && (
        <BatchIngestProgressModal
          open
          files={batchIngest.files}
          tradingDay={batchIngest.tradingDay}
          folderName={batchIngest.folderName}
          apiPath={`${apiBase}/run`}
          folderPath={batchIngest.folderPath}
          onClose={() => setBatchIngest(null)}
          onComplete={async () => {
            setBatchIngest(null)
            await load()
            if (isAzure) await loadAzure({ explicit: true, year: azureNav.year, month: azureNav.month })
          }}
        />
      )}
    </div>
  )
}
